/**
 * MeetingMinutesTab — 「会议纪要」页签：七段式纪要（S11+）或旧结构摘要，
 * 未确认时顶部给出醒目的「AI 草稿 · 未经律师确认」提示。
 *
 * 产出内容从 `meeting.summary` 读取，不读状态轮询响应的 `result`字段——
 * 这样某一次生成未完成时，上一次的产出仍然照常展示（与 MeetingAiActionStatus
 * 的既有约定一致）。
 */

import { useMemo } from "react";
import { List, Tag, Typography } from "antd";
import type { MeetingAnalysisState } from "../../types/meeting";
import { MEETING_SUMMARY_V2_SECTIONS, isSevenSectionMeetingSummary } from "../../types/meeting";
import { parseMeetingSummary } from "../../utils/meetingSummary";
import EmptyState from "../common/EmptyState";
import MeetingAiActionStatus from "./MeetingAiActionStatus";

const { Paragraph, Text, Title } = Typography;

const TONE_LABELS: Record<string, { label: string; color: string }> = {
  positive: { label: "达成共识", color: "green" },
  neutral: { label: "信息同步", color: "blue" },
  cautious: { label: "方向谨慎", color: "orange" },
  contentious: { label: "存在分歧", color: "red" },
};

interface MeetingMinutesTabProps {
  summaryRaw: string | null;
  confirmedAt: string | null;
  hasTranscript: boolean;
  noTranscriptReason: string;
  state: MeetingAnalysisState;
  errorMessage: string | null;
  completedAt: string | null;
  submitting: boolean;
  onSubmit: () => void;
}

export default function MeetingMinutesTab({
  summaryRaw,
  confirmedAt,
  hasTranscript,
  noTranscriptReason,
  state,
  errorMessage,
  completedAt,
  submitting,
  onSubmit,
}: MeetingMinutesTabProps) {
  const summary = useMemo(() => parseMeetingSummary(summaryRaw), [summaryRaw]);

  return (
    <div>
      {!confirmedAt && (
        <div
          data-testid="meeting-minutes-draft-banner"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--jerry-sp-2)",
            padding: "var(--jerry-sp-2) var(--jerry-sp-3)",
            marginBottom: "var(--jerry-sp-3)",
            background: "var(--jerry-warning-subtle)",
            border: "1px solid var(--jerry-warning-500)",
            borderRadius: 6,
          }}
        >
          <Text strong style={{ color: "var(--jerry-warning-700)" }}>
            AI 草稿 · 未经律师确认
          </Text>
          <Text type="secondary" style={{ fontSize: "var(--jerry-font-size-sm)" }}>
            以下内容由 AI 生成，确认之前不写入日程、不进卷宗、不可对外分享。
          </Text>
        </div>
      )}

      <MeetingAiActionStatus
        action="summarize"
        state={state}
        errorMessage={errorMessage}
        completedAt={completedAt}
        hasPreviousResult={!!summary}
        onSubmit={onSubmit}
        submitting={submitting}
        blocked={!hasTranscript}
        blockedReason={!hasTranscript ? noTranscriptReason : undefined}
      />

      {!summary ? (
        <EmptyState
          title="尚未生成会议纪要"
          description="导入会议纪要后，点击「生成会后总结」提取一页摘要、议题、事实、风险、决策、待核实问题与下一步工作。"
        />
      ) : isSevenSectionMeetingSummary(summary) ? (
        <div data-testid="meeting-minutes-v2">
          <Title level={5}>一页摘要</Title>
          <Paragraph
            style={{
              background: "var(--jerry-gray-100)",
              padding: "var(--jerry-sp-3)",
              borderRadius: 6,
              whiteSpace: "pre-wrap",
            }}
          >
            {summary.onePageSummary}
          </Paragraph>
          {MEETING_SUMMARY_V2_SECTIONS.map(({ key, label }) => {
            const items = summary[key];
            if (!items || items.length === 0) return null;
            return (
              <div key={key}>
                <Title level={5}>{label}</Title>
                <List
                  size="small"
                  dataSource={items}
                  renderItem={(item, idx) => (
                    <List.Item key={idx}>
                      <Text>{item}</Text>
                    </List.Item>
                  )}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div data-testid="meeting-minutes-legacy">
          <Paragraph>
            <Tag color={TONE_LABELS[summary.overallTone]?.color ?? "blue"}>
              {TONE_LABELS[summary.overallTone]?.label ?? "信息同步"}
            </Tag>
          </Paragraph>
          <Paragraph
            style={{
              background: "var(--jerry-gray-100)",
              padding: "var(--jerry-sp-3)",
              borderRadius: 6,
              whiteSpace: "pre-wrap",
            }}
          >
            {summary.summary}
          </Paragraph>
          {(summary.keyPoints ?? []).length > 0 && (
            <>
              <Title level={5}>关键讨论点</Title>
              <List
                itemLayout="vertical"
                dataSource={summary.keyPoints ?? []}
                renderItem={(kp, idx) => (
                  <List.Item key={idx}>
                    <Text strong>{kp.topic}</Text>
                    <br />
                    <Text type="secondary">{kp.detail}</Text>
                  </List.Item>
                )}
              />
            </>
          )}
          {(summary.decisions ?? []).length > 0 && (
            <>
              <Title level={5}>决议事项</Title>
              <List
                itemLayout="vertical"
                dataSource={summary.decisions ?? []}
                renderItem={(d, idx) => (
                  <List.Item key={idx}>
                    <Text>{d.text}</Text>
                    {d.rationale && (
                      <>
                        <br />
                        <Text type="secondary">依据：{d.rationale}</Text>
                      </>
                    )}
                  </List.Item>
                )}
              />
            </>
          )}
          {(summary.actionItemsBrief ?? []).length > 0 && (
            <>
              <Title level={5}>行动项概览</Title>
              <List
                itemLayout="vertical"
                dataSource={summary.actionItemsBrief ?? []}
                renderItem={(a, idx) => (
                  <List.Item key={idx}>
                    <Text>{a.text}</Text>
                    {a.assignee && <Tag color="purple">{a.assignee}</Tag>}
                    {a.due && <Tag>截止 {a.due}</Tag>}
                  </List.Item>
                )}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
